import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { Modal } from '../../../components/common/Modal';
import { PageHeader } from '../../../components/common/PageHeader';
import { Table, type IColumn } from '../../../components/common/Table';
import { SchedulesTabs } from '../../../components/workflows/SchedulesTabs';
import { cronPresets, getBrowserTimezone, timezones } from '../../../lib/cron-presets';
import { formatDateTime, formatRelative } from '../../../lib/format';
import {
  useTheopolis,
  type IAgent,
  type ICreateScheduleBody,
  type ISchedule,
  type IUpdateScheduleBody,
  type ScheduleStatus,
} from '../../../lib/theopolis';

export const Route = createFileRoute('/refinement/schedules/')({ component: SchedulesPage });

function SchedulesPage() {
  const theo = useTheopolis();
  const qc = useQueryClient();

  const agentsQuery = useQuery({
    queryKey: ['theopolis', 'agents'],
    queryFn: () => theo.get('agents').json<IAgent[]>(),
  });
  const schedulesQuery = useQuery({
    queryKey: ['theopolis', 'schedules'],
    queryFn: () => theo.get('schedules').json<ISchedule[]>(),
    refetchInterval: 30_000,
  });

  const agents = agentsQuery.data ?? [];
  const schedules = schedulesQuery.data ?? [];
  // Only refinement-typed agents can be scheduled (the API enforces this
  // at create time). The "New schedule" button gates on this so users
  // don't get a confusing 400 after filling out the form.
  const hasSchedulableAgent = agents.some((a) => a.type === 'refinement');

  const setStatus = useMutation({
    mutationFn: (vars: { id: string; status: ScheduleStatus }) =>
      theo
        .patch(`schedules/${vars.id}`, {
          json: { status: vars.status } satisfies IUpdateScheduleBody,
        })
        .json<ISchedule>(),
    onSuccess: async () => {
      try {
        await qc.invalidateQueries({ queryKey: ['theopolis', 'schedules'] });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => theo.delete(`schedules/${id}`),
    onSuccess: async () => {
      try {
        await qc.invalidateQueries({ queryKey: ['theopolis', 'schedules'] });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const [editing, setEditing] = useState<ISchedule | 'new' | null>(null);

  const cols: IColumn<ISchedule>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (s) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}>
            {s.name}
          </span>
          <span className="t-mono-meta t-xs">
            {agents.find((a) => a.id === s.agentId)?.name ?? s.agentId.slice(0, 12)}
          </span>
        </div>
      ),
    },
    {
      key: 'cron',
      header: 'Cron',
      cell: (s) => <code className="t-mono">{s.cronExpression}</code>,
      width: 160,
    },
    {
      key: 'tz',
      header: 'Timezone',
      cell: (s) => s.timezone ?? 'UTC',
      mono: true,
      width: 130,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (s) => <StatusPill status={s.status} />,
      width: 110,
    },
    {
      key: 'next',
      header: 'Next run',
      cell: (s) => formatDateTime(s.nextRunAt),
      width: 200,
    },
    {
      key: 'last',
      header: 'Last fire',
      cell: (s) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span>{formatRelative(s.lastFiredAt)}</span>
          {s.lastError ? (
            <span style={{ color: 'var(--status-danger)' }} className="t-xs">
              {truncate(s.lastError, 80)}
            </span>
          ) : null}
        </div>
      ),
      width: 220,
    },
    {
      key: 'actions',
      header: '',
      cell: (s) => (
        <div style={{ display: 'flex', gap: 'var(--sp-1)', justifyContent: 'flex-end' }}>
          {s.status === 'PAUSED' ? (
            <button
              type="button"
              className="btn btn--outline btn--xs"
              onClick={() => setStatus.mutate({ id: s.id, status: 'ACTIVE' })}
            >
              Resume
            </button>
          ) : s.status === 'ACTIVE' ? (
            <button
              type="button"
              className="btn btn--outline btn--xs"
              onClick={() => setStatus.mutate({ id: s.id, status: 'PAUSED' })}
            >
              Pause
            </button>
          ) : null}
          <button type="button" className="btn btn--ghost btn--xs" onClick={() => setEditing(s)}>
            Edit
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={() => {
              if (window.confirm(`Delete schedule "${s.name}"?`)) remove.mutate(s.id);
            }}
          >
            Delete
          </button>
        </div>
      ),
      width: 220,
    },
  ];

  return (
    <>
      <PageHeader
        title="Scheduled sessions"
        actions={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setEditing('new')}
            disabled={!hasSchedulableAgent}
            title={hasSchedulableAgent ? 'New schedule' : 'No refinement-typed agents available'}
          >
            New schedule
          </button>
        }
      />
      <SchedulesTabs />
      <Table<ISchedule>
        rows={schedules}
        columns={cols}
        rowKey={(s) => s.id}
        isLoading={schedulesQuery.isLoading}
        error={(schedulesQuery.error as Error | null) ?? null}
        emptyTitle="No schedules yet"
        emptyDescription="Schedule a prompt to run on a recurring cadence."
      />

      <ScheduleModal
        editing={editing}
        agents={agents}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          fork(qc.invalidateQueries({ queryKey: ['theopolis', 'schedules'] }));
        }}
      />
    </>
  );
}

function StatusPill({ status }: { status: ScheduleStatus }) {
  const cls =
    status === 'ACTIVE'
      ? 'badge badge--success'
      : status === 'PAUSED'
        ? 'badge badge--warning'
        : 'badge badge--neutral';
  return <span className={cls}>{status}</span>;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

// ── Create / Edit modal ──────────────────────────────────────────────────
function ScheduleModal({
  editing,
  agents,
  onClose,
  onSaved,
}: {
  editing: ISchedule | 'new' | null;
  agents: IAgent[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const theo = useTheopolis();
  const open = editing !== null;
  const isEdit = editing !== 'new' && editing !== null;
  const existing = isEdit ? (editing as ISchedule) : null;

  // Schedules can only fire refinement-typed agents (the API enforces this
  // at create time too — see apps/theopolis ScheduleService.create). Filter
  // here so the dropdown can't surface chat-typed or future-typed agents
  // that would just 400 on submit.
  //
  // useMemo is critical: this array is a dep of the form-seed useEffect
  // below. Without memoization, every render recomputes a new reference,
  // the effect fires every render, and any state change (e.g. clicking
  // a cron preset) immediately gets stomped by the re-seed.
  const eligibleAgents = useMemo(() => agents.filter((a) => a.type === 'refinement'), [agents]);

  const [name, setName] = useState('');
  const [agentId, setAgentId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [cron, setCron] = useState('0 9 * * *');
  const [tz, setTz] = useState(getBrowserTimezone());
  const [titleTemplate, setTitleTemplate] = useState('');

  // Re-seed local state when the editing target changes.
  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setAgentId(existing.agentId);
      setPrompt(existing.prompt);
      setCron(existing.cronExpression);
      setTz(existing.timezone ?? '');
      setTitleTemplate(existing.titleTemplate ?? '');
    } else if (editing === 'new') {
      setName('');
      setAgentId(eligibleAgents[0]?.id ?? '');
      setPrompt('');
      setCron('0 9 * * *');
      setTz(getBrowserTimezone());
      setTitleTemplate('');
    }
  }, [editing, existing, eligibleAgents]);

  const save = useMutation({
    mutationFn: () => {
      if (existing) {
        // Agent is immutable after create — only PATCH the editable subset.
        const body: IUpdateScheduleBody = {
          name: name.trim(),
          prompt: prompt.trim(),
          cronExpression: cron.trim(),
          timezone: tz.trim() || null,
          titleTemplate: titleTemplate.trim() || null,
        };
        return theo.patch(`schedules/${existing.id}`, { json: body }).json<ISchedule>();
      }
      const body: ICreateScheduleBody = {
        agentId,
        name: name.trim(),
        prompt: prompt.trim(),
        cronExpression: cron.trim(),
        timezone: tz.trim() || null,
        titleTemplate: titleTemplate.trim() || null,
      };
      return theo.post('schedules', { json: body }).json<ISchedule>();
    },
    onSuccess: () => onSaved(),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !prompt.trim() || !cron.trim() || !agentId) return;
    save.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={existing ? 'EDIT SCHEDULE' : 'NEW SCHEDULE'}
      title={existing ? existing.name : 'New schedule'}
      width="min(720px, 92vw)"
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 'var(--sp-3)',
          }}
        >
          <label className="field">
            <span className="field-label">Name</span>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required={true}
            />
          </label>

          <label className="field">
            <span className="field-label">Agent</span>
            <select
              className="select-native"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              disabled={Boolean(existing)}
              required={true}
            >
              {eligibleAgents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <span className="field-help">Only refinement-typed agents can be scheduled.</span>
          </label>
        </div>

        <label className="field">
          <span className="field-label">Prompt</span>
          <textarea
            className="textarea"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            required={true}
            placeholder="What should the agent do every fire?"
          />
        </label>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 'var(--sp-3)',
          }}
        >
          {/* The cron field is a plain <div> (not <label>) because the
              preset chip buttons live inside it. A <button> nested in a
              <label> that wraps an <input> can have its click swallowed
              by the label's redirect-to-input behavior, so the preset
              click never updates state. */}
          <div className="field">
            <label className="field-label" htmlFor="schedule-cron-input">
              Cron expression
            </label>
            <input
              id="schedule-cron-input"
              type="text"
              className="input"
              value={cron}
              onChange={(e) => setCron(e.target.value)}
              required={true}
              placeholder="min hour dom mon dow"
            />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--sp-1)',
                marginTop: 'var(--sp-1)',
              }}
            >
              {cronPresets.map((p) => (
                <button
                  key={p.expr}
                  type="button"
                  className={`btn btn--xs ${cron === p.expr ? 'btn--primary' : 'btn--outline'}`}
                  onClick={() => setCron(p.expr)}
                  title={p.expr}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <span className="field-help">
              5-field cron (e.g. <code>0 9 * * *</code> = daily 09:00).
            </span>
          </div>
          <label className="field">
            <span className="field-label">Timezone</span>
            <select className="select-native" value={tz} onChange={(e) => setTz(e.target.value)}>
              {timezones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
            <span className="field-help">IANA name. Defaults to your browser&apos;s timezone.</span>
          </label>
        </div>

        <label className="field">
          <span className="field-label">Title template (optional)</span>
          <input
            type="text"
            className="input"
            value={titleTemplate}
            onChange={(e) => setTitleTemplate(e.target.value)}
            placeholder="{{name}} — {{date}}"
          />
          <span className="field-help">
            Supports <code>{'{{name}}'}</code> and <code>{'{{date}}'}</code> placeholders.
          </span>
        </label>

        {save.error ? <div className="field-error">{(save.error as Error).message}</div> : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary btn--sm" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : existing ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
